import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useEvaluateSubmission } from '@/features/memorization/hooks';
import { RUBRIC_SCORE_LABELS } from '@/features/memorization/labels';
import type {
  EvaluateSegmentInput,
  EvaluationOutcome,
  RubricScore,
  SubmissionView,
} from '@/features/memorization/types';
import { ApiError } from '@/lib/api/client';

interface DraftSegment {
  readonly startAyah: string;
  readonly endAyah: string;
  readonly outcome: EvaluationOutcome;
  readonly scoreAccuracy: RubricScore | '';
  readonly scoreFluency: RubricScore | '';
  readonly scoreTajwid: RubricScore | '';
  readonly scoreConfidence: RubricScore | '';
  readonly notes: string;
}

const RUBRIC_OPTIONS: readonly RubricScore[] = ['EXCELLENT', 'GOOD', 'FAIR', 'NEEDS_IMPROVEMENT'];

function emptySegment(startAyah: number, endAyah: number): DraftSegment {
  return {
    startAyah: String(startAyah),
    endAyah: String(endAyah),
    outcome: 'ACCEPTED',
    scoreAccuracy: '',
    scoreFluency: '',
    scoreTajwid: '',
    scoreConfidence: '',
    notes: '',
  };
}

/**
 * `POST /memorization/setoran/:id/evaluations` (Phase G-2, Job 13). The
 * common case is one segment covering the submission's full range; a
 * Teacher may split into multiple segments for a partial accept/reject.
 * The backend is the real validator for tiling (segments must exactly
 * cover the submission's range, no gaps/overlaps) - this form only
 * defaults sensibly, it never fabricates that rule client-side.
 */
export function EvaluateSubmissionDialog({
  submission,
  open,
  onOpenChange,
  onEvaluated,
}: {
  readonly submission: SubmissionView;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onEvaluated?: () => void;
}): JSX.Element {
  const [segments, setSegments] = useState<readonly DraftSegment[]>([
    emptySegment(submission.startAyah, submission.endAyah),
  ]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const evaluate = useEvaluateSubmission(submission.studentId, submission.id, submission.targetId);

  function reset(): void {
    setSegments([emptySegment(submission.startAyah, submission.endAyah)]);
    setValidationError(null);
    evaluate.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  function updateSegment(index: number, patch: Partial<DraftSegment>): void {
    setSegments((current) =>
      current.map((segment, i) => (i === index ? { ...segment, ...patch } : segment)),
    );
  }

  function addSegment(): void {
    const last = segments[segments.length - 1];
    const nextStart = last !== undefined ? Number(last.endAyah) + 1 : submission.startAyah;
    setSegments((current) => [...current, emptySegment(nextStart, submission.endAyah)]);
  }

  function removeSegment(index: number): void {
    setSegments((current) => current.filter((_, i) => i !== index));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    const parsed: EvaluateSegmentInput[] = [];
    for (const segment of segments) {
      const startAyahNumber = Number(segment.startAyah);
      const endAyahNumber = Number(segment.endAyah);
      if (!Number.isInteger(startAyahNumber) || startAyahNumber < 1) {
        setValidationError('Masukkan ayat awal yang valid pada setiap segmen.');
        return;
      }
      if (!Number.isInteger(endAyahNumber) || endAyahNumber < 1) {
        setValidationError('Masukkan ayat akhir yang valid pada setiap segmen.');
        return;
      }
      if (endAyahNumber < startAyahNumber) {
        setValidationError('Ayat akhir tidak boleh lebih kecil dari ayat awal.');
        return;
      }
      if (
        segment.scoreAccuracy === '' ||
        segment.scoreFluency === '' ||
        segment.scoreTajwid === '' ||
        segment.scoreConfidence === ''
      ) {
        setValidationError('Lengkapi seluruh skor penilaian pada setiap segmen.');
        return;
      }
      parsed.push({
        startAyah: startAyahNumber,
        endAyah: endAyahNumber,
        outcome: segment.outcome,
        scoreAccuracy: segment.scoreAccuracy,
        scoreFluency: segment.scoreFluency,
        scoreTajwid: segment.scoreTajwid,
        scoreConfidence: segment.scoreConfidence,
        notes: segment.notes.trim().length > 0 ? segment.notes.trim() : null,
      });
    }

    evaluate.mutate(
      { segments: parsed },
      {
        onSuccess: () => {
          handleOpenChange(false);
          onEvaluated?.();
        },
      },
    );
  }

  const apiErrorMessage =
    evaluate.error instanceof ApiError
      ? evaluate.error.message
      : evaluate.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Evaluasi Setoran</DialogTitle>
          <DialogDescription>
            Surah {submission.surah}: Ayat {submission.startAyah}
            {submission.endAyah !== submission.startAyah ? `-${submission.endAyah}` : ''}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {segments.map((segment, index) => (
            <div key={index} className="flex flex-col gap-3 rounded-md border border-border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">Segmen {index + 1}</span>
                {segments.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      removeSegment(index);
                    }}
                  >
                    Hapus
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`segment-${String(index)}-start`}>Ayat Awal</Label>
                  <Input
                    id={`segment-${String(index)}-start`}
                    type="number"
                    min={1}
                    value={segment.startAyah}
                    onChange={(event) => {
                      updateSegment(index, { startAyah: event.target.value });
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`segment-${String(index)}-end`}>Ayat Akhir</Label>
                  <Input
                    id={`segment-${String(index)}-end`}
                    type="number"
                    min={1}
                    value={segment.endAyah}
                    onChange={(event) => {
                      updateSegment(index, { endAyah: event.target.value });
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor={`segment-${String(index)}-outcome`}>Hasil</Label>
                <Select
                  value={segment.outcome}
                  onValueChange={(value) => {
                    updateSegment(index, { outcome: value as EvaluationOutcome });
                  }}
                >
                  <SelectTrigger id={`segment-${String(index)}-outcome`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACCEPTED">Diterima</SelectItem>
                    <SelectItem value="NEEDS_REVISION">Perlu Perbaikan</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    ['scoreAccuracy', 'Akurasi'],
                    ['scoreFluency', 'Kelancaran'],
                    ['scoreTajwid', 'Tajwid'],
                    ['scoreConfidence', 'Kepercayaan Diri'],
                  ] as const
                ).map(([field, label]) => (
                  <div key={field} className="flex flex-col gap-1">
                    <Label htmlFor={`segment-${String(index)}-${field}`}>{label}</Label>
                    <Select
                      value={segment[field]}
                      onValueChange={(value) => {
                        updateSegment(index, { [field]: value as RubricScore });
                      }}
                    >
                      <SelectTrigger id={`segment-${String(index)}-${field}`}>
                        <SelectValue placeholder="Pilih" />
                      </SelectTrigger>
                      <SelectContent>
                        {RUBRIC_OPTIONS.map((score) => (
                          <SelectItem key={score} value={score}>
                            {RUBRIC_SCORE_LABELS[score]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor={`segment-${String(index)}-notes`}>Catatan (opsional)</Label>
                <Textarea
                  id={`segment-${String(index)}-notes`}
                  value={segment.notes}
                  onChange={(event) => {
                    updateSegment(index, { notes: event.target.value });
                  }}
                />
              </div>
            </div>
          ))}

          <Button type="button" variant="outline" size="sm" onClick={addSegment}>
            Tambah Segmen
          </Button>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={evaluate.isPending}>
              {evaluate.isPending ? 'Menyimpan...' : 'Simpan Evaluasi'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
